/**
 * @ComponentFor ImageBlockViewModel
 */

import connect from 'Shared/connect';
import { isCompact as functionIsCompact } from 'Shared/Viewport';
import React from 'react';
import { styled } from '@glitz/react';
import ImageBlockViewModel from './ImageBlockViewModel.type';
import { AspectRatioInner, AspectRatioOuter } from 'Shared/SharedComponents/atoms/AspectRatio';
import { GridWrap } from 'Shared/PageLayout';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import SwiperList from 'Shared/SwiperList';
import * as style from 'Shared/Style';
import ImageLinkViewModel from 'Shared/ViewModels/ImageLinkViewModel.type';
import Link from 'Shared/Link';

const Image = styled.img({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center center',
})

const AutoWidthImage = styled.img({
  width: 'auto',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center center',
})

const ImageWrapOne = styled(AspectRatioOuter, {
  borderRadius: '8px',
  paddingTop: '80%'
})

const ImageWrapTwo = styled(AspectRatioOuter, {
  borderRadius: '8px',
  paddingTop: '125%',
  width: '100%'
})

const ImageWrapMany = styled(AspectRatioOuter, {
  borderRadius: '8px',
  paddingTop: '125%'
})

const TwoImageWrap = styled.div({
  ['@media ' + style.mediaUpToLarge]: {
    rowGap: '12px',
  },
})

export const ImageLink = styled(Link, {
  color: 'inherit',
  textDecoration: 'none',
});

type LinkedImageProps = React.HTMLAttributes<HTMLElement> & {
  item: ImageLinkViewModel;
};

const LinkedImage = (props: LinkedImageProps) => {
  if (props.item.url) {
    return (
      <ImageLink
        to={props.item.url}
        title={props.item.titleText}
      >
        {props.children}
      </ImageLink>
    );
  }

  return (
    <>{props.children}</>
  );
}

export const ImageBlock = connect(
  state => ({
    currentBreakpoint: state.currentBreakpoint,
  }),
  dispatch => ({}),
)((props: ImageBlockViewModel & { currentBreakpoint: number }) => {
  const { currentBreakpoint } = props;
  const isCompact = functionIsCompact(currentBreakpoint);
  const defaultYPadding = props.noStandardMargins ? '0px' : (isCompact ? '40px' : '80px');

  if (props.items?.length === 1)
    return (
      <GridWrap className='grid wsb' css={{ padding: { y: defaultYPadding } }}>
        <div className={createGridClasses(12, 8, createOffsetGridClassLarge(props.noStandardMargins ? 0 : 3))}>
          <LinkedImage item={props.items[0]}>
            <ImageWrapOne>
              <AspectRatioInner>
                <Image
                  src={props.items[0].contentLink.url}
                  alt={props.items[0].alternateText}
                />
              </AspectRatioInner>
            </ImageWrapOne>
          </LinkedImage>
        </div>
      </GridWrap>
    );

  if (props.items?.length === 2)
    return (
      <GridWrap className='grid wsb' css={{ padding: { y: defaultYPadding } }}>
        <TwoImageWrap className={createGridClasses(12, 8, createOffsetGridClassLarge(props.noStandardMargins ? 0 : 3) + ' grid subgrid-10')}>
          {props.items?.map((item, index) => (
            <div key={index} className={createGridClasses(12, 5)}>
              <LinkedImage item={item}>
                <ImageWrapTwo>
                  <AspectRatioInner>
                    <AutoWidthImage
                      src={item.contentLink.url}
                      alt={item.alternateText}
                    />
                  </AspectRatioInner>
                </ImageWrapTwo>
              </LinkedImage>
            </div>
          ))}
        </TwoImageWrap>
      </GridWrap>
    );

  return (
    <GridWrap className='grid wsb' css={{ padding: { y: defaultYPadding } }}>
      <div className={createGridClasses(12, 10, createOffsetGridClassLarge(props.noStandardMargins ? 0 : 2))}>
        <div>
          <SwiperList
            slidesPerView={isCompact ? 1.1 : 3}
            spaceBetween={isCompact ? 12 : 16}
            showNav={false}
            cssMode={true}
            isCompact={isCompact}
          >
            {props.items?.map((item, index) => (
              <div className='swiper-slide' key={index}>
                <LinkedImage item={item}>
                  <ImageWrapMany key={index}>
                    <AspectRatioInner>
                      <AutoWidthImage
                        src={item.contentLink.url}
                        alt={item.alternateText}
                      />
                    </AspectRatioInner>
                  </ImageWrapMany>
                </LinkedImage>
              </div>
            ))}
          </SwiperList>
        </div>
      </div>
    </GridWrap>
  );
});

export default ImageBlock;
